import { Block, CheckCircle, Delete, Edit } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";

type CategoryItem = {
  id: number | string;
  name: string;
  active?: boolean;
};

type CategoryListProps = {
  items: CategoryItem[];
  onToggleActive?: (item: CategoryItem) => void | Promise<void>;
  onDelete?: (item: CategoryItem) => void | Promise<void>;
  onEdit?: (item: CategoryItem) => void;
  emptyMessage?: string;
};

export default function CategoryList({
  items,
  onToggleActive,
  onDelete,
  onEdit,
  emptyMessage,
}: CategoryListProps) {
  const renderEmpty = !items || items.length === 0;

  return (
    <>
      {renderEmpty ? (
        <Box sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body2">
            {emptyMessage || "Nenhuma categoria encontrada."}
          </Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {items.map((x: CategoryItem, idx: number) => {
            const first =
              String(x?.name || "")
                .charAt(0)
                .toUpperCase() || "?";
            return (
              <React.Fragment key={x.id}>
                <ListItem
                  alignItems="center"
                  sx={{ pr: { xs: 11, sm: 8 }, pl: 0 }}
                  secondaryAction={
                    onToggleActive || onDelete ? (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ flexWrap: "nowrap" }}
                      >
                        <Chip
                          label={x.active ? "Ativa" : "Inativa"}
                          color={x.active ? "success" : "default"}
                          size="small"
                        />
                        {onToggleActive && (
                          <Tooltip
                            title={
                              x.active
                                ? "Desativar categoria"
                                : "Ativar categoria"
                            }
                          >
                            <IconButton
                              edge="end"
                              aria-label="toggle-active"
                              onClick={() => onToggleActive(x)}
                            >
                              {x.active ? (
                                <Block color="warning" />
                              ) : (
                                <CheckCircle color="success" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {onEdit && (
                          <Tooltip title="Editar categoria">
                            <IconButton
                              edge="end"
                              aria-label="edit"
                              onClick={() => onEdit(x)}
                            >
                              <Edit color="primary" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDelete && (
                          <Tooltip title="Deletar categoria">
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => onDelete(x)}
                            >
                              <Delete color="error" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    ) : undefined
                  }
                >
                  <ListItemAvatar>
                    <Avatar>{first}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={x.name}
                    secondary={`ID: ${x.id}`}
                    primaryTypographyProps={{
                      noWrap: false,
                      sx: { maxWidth: { xs: "60%", sm: "100%" } },
                    }}
                    secondaryTypographyProps={{
                      color: "text.secondary",
                      sx: { display: { xs: "none", sm: "inline" } },
                    }}
                  />
                </ListItem>
                {idx < items.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
        </List>
      )}
    </>
  );
}
